import { uniqueId } from "lodash";

import { Icon } from "@/DesignSystem";
import { InputWithIcon } from "@/DesignSystem/Molecules/ToolBox/InputWithIcon";
import { DesignSystemLocale, getText } from "@/DesignSystem/Vocab/TranslationManager";
import { SearchFieldContainer } from "../Components/SearchFieldContainer";

import MagnifyingGlass from "@/DesignSystem/Atoms/Icons/Types/MagnifyingGlass/magnifying-glass-regular.svg";

type SearchInputLabelPosition = "top" | "left";

interface ISearchInputProps {
    /**
     * The optional Search input ID.
     */
    id?: string;

    /**
     * An ID to target in frontend tests.
     */
    dataCy?: string;

    /**
   * The width of the SearchInput in pixels.
   */
    width?: string;

    /**
   * Label text displayed for the SearchInput: can be positioned on top, left, or omitted.
   */
    label?: string;

    /**
   * Position of the label: 'top' or 'left'.
   */
    labelPosition?: SearchInputLabelPosition;

    /**
   * Function triggered when the input value changes.
   */
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;

    /**
   * Locale used to render placeholder text's language.
   */
    locale?: DesignSystemLocale;

    /**
     * Whether the input is disabled.
     */
    disabled?: boolean;

    /**
   * The value of the input.
   */
    value?: string;
}

/**
 * The Search input.
 * @param {ISearchInputProps} param The props used to render the Search input.
 * @returns The rendered component.
 */
function SearchInput({
    id,
    dataCy,
    width,
    label,
    labelPosition = "top",
    value,
    onChange,
    locale = "en",
    disabled = false
}: Readonly<ISearchInputProps>): JSX.Element {
    const inputId = id || `input-${uniqueId()}`;
    const placeholder = getText(locale, "searchPlaceholder");

    return (
        <SearchFieldContainer
            id={inputId}
            dataCy={dataCy}
            width={width}
            label={label}
            labelPosition={labelPosition}
        >
            <InputWithIcon
                id={inputId}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                disabled={disabled}
                icon={(
                    <Icon colorVariant="neutral" size="sm">
                        <MagnifyingGlass />
                    </Icon>
                )}
                iconPosition="left"
            />
        </SearchFieldContainer>
    );
}

export { ISearchInputProps, SearchInput, SearchInputLabelPosition };
